import SearchIcon from '@mui/icons-material/Search';
import { ButtonBase, Card, IconButton, InputAdornment, Stack, TextField, useMediaQuery, useTheme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionService } from '../../../services/transaction';
import { categoriesService } from '../../../services/categoriesServices';

// Icon imports
import BabyChangingStationIcon from '@mui/icons-material/BabyChangingStation';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import HomeIcon from '@mui/icons-material/Home';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import NightShelterIcon from '@mui/icons-material/NightShelter';
import OtherHousesIcon from '@mui/icons-material/OtherHouses';
import PetsIcon from '@mui/icons-material/Pets';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import useOpenListCategoryModal from '../../../hooks/ui-states/useOpenListCategoryModal';
import BaseDialog from '../../ui/BaseDialog';
import DrawerBottom from '../../ui/DrawerBottom/DrawerBottom';
import { KidStarIcon, PencilSimpleSlashIcon } from '../../ui/icons';
import DynamicSvgIcon from '../../ui/icons/DynamicSvgIcon';

interface CategorySelectorProps {
  open: boolean;
  onClose: () => void;
  onSelectCategory: (category: any) => void;
  type: 'income' | 'expense';
  transactionType?: 'expense' | 'income' | 'transfer' | 'loan' | 'credit_card';
  loanType?: string | null;
}

const CategorySelector = ({
  open,
  onClose,
  onSelectCategory,
  type,
  transactionType,
  loanType
}: CategorySelectorProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['categorySelector']);
  const [, setOpenEditCategory] = useOpenListCategoryModal();
  const [categoryItem, setCategoryItem] = useState<any[]>([]);
  const [frequentlyUsedCategories, setFrequentlyUsedCategories] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Use theme colors instead of hardcoded hex values
  const colors = [
    theme.palette.pink[500],
    theme.palette.cyan[500],
    theme.palette.purple[500],
    theme.palette.orange[500],
    theme.palette.teal[500],
    theme.palette.navy[500],
    theme.palette.green[500],
    theme.palette.blue[500],
    theme.palette.coral[500],
    theme.palette.yellow[500],
  ];

  const categoryIconMap: { [key: string]: JSX.Element } = {
    [t('food', { ns: 'categorySelector' })]: <FastfoodIcon />,
    [t('housing', { ns: 'categorySelector' })]: <HomeIcon />,
    [t('family', { ns: 'categorySelector' })]: <BabyChangingStationIcon />,
    [t('transportation', { ns: 'categorySelector' })]: <ElectricBoltIcon />,
    [t('health', { ns: 'categorySelector' })]: <PetsIcon />,
    [t('self_development', { ns: 'categorySelector' })]: <OtherHousesIcon />,
    [t('lifestyle', { ns: 'categorySelector' })]: <NightShelterIcon />,
    [t('giving', { ns: 'categorySelector' })]: <RestaurantIcon />,
    [t('other', { ns: 'categorySelector' })]: <LunchDiningIcon />,
  };

  let indexTracker = 0;

  const sequentialItem = (arr: any) => {
    if (arr.length === 0) return null;
    const item = arr[indexTracker % arr.length];
    indexTracker++;
    return item;
  };

  useEffect(() => {
    if (open) {
      const fetchCategory = async () => {
        try {
          const [categoriesResponse, frequentResponse] = await Promise.all([
            type === 'expense'
              ? TransactionService.getExpenseCategory()
              : TransactionService.getIncomeCategory(),
            type === 'expense'
              ? categoriesService.getFrequentlyUsedExpenseCategories(8)
              : categoriesService.getFrequentlyUsedIncomeCategories(8),
          ]);

          setCategoryItem(categoriesResponse.data);
          setFrequentlyUsedCategories(frequentResponse.data || []);
        } catch (error) {
          console.log(error);
        }
      };
      fetchCategory();
    }
  }, [open, type]);

  const handleSelectCategory = (subCategory: any, parentCategory: any) => {
    onSelectCategory({
      id: subCategory.id,
      name: subCategory.name,
      category: parentCategory.name,
      icon: subCategory.icon,
      iconColor: subCategory.iconColor || parentCategory.iconColor,
      backgroundColor: subCategory.backgroundColor || subCategory.iconColor || parentCategory.iconColor,
    });
    onClose();
  };

  const handleSelectFrequentCategory = (category: any) => {
    onSelectCategory({
      id: category.id,
      name: category.name,
      category: category.categoryGroupName,
      icon: category.icon,
      iconColor: category.iconColor,
      backgroundColor: category.backgroundColor || category.iconColor,
    });
    onClose();
  };

  // Filter frequently used categories based on search term
  const filteredFrequentlyUsed = frequentlyUsedCategories.filter((category: any) =>
    normalizeForSearch(category.name).includes(normalizeForSearch(searchTerm))
  );

  const renderFrequentlyUsedSection = () => {
    if (filteredFrequentlyUsed.length === 0 || type !== 'expense') return null;

    return (
      <Accordion defaultExpanded sx={{ boxShadow: 'none' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="frequently-used-content"
          id="frequently-used-header"
          sx={{
            px: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '50%',
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.yellow[100],
              }}
            >
              <KidStarIcon style={{ color: theme.palette.yellow[500], fontSize: 18 }} />
            </Card>
            <Typography component="span" variant="h2">
              {t('frequently_used', { ns: 'categorySelector', defaultValue: 'Hay sử dụng' })}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            '&.MuiAccordionDetails-root': {
              padding: 0,
            },
          }}
        >
          <Stack direction="column" spacing={2} pt={2}>
            <Stack display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} justifyContent="center">
              {filteredFrequentlyUsed.map((category: any, index: number) => (
                <ButtonBase
                  key={index}
                  sx={{
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'start',
                    color: category.backgroundColor || category.iconColor,
                    '&:hover': {
                      color: category.backgroundColor || category.iconColor,
                    },
                  }}
                  onClick={() => handleSelectFrequentCategory(category)}
                >
                  <Stack direction="column" justifyContent="start" alignItems="center" spacing={1} sx={{ cursor: "pointer" }}>
                    <Card
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: category.backgroundColor || category.iconColor,
                      }}
                    >
                      <img src={category.icon} alt={category.name} style={{ width: 24, height: 24 }} />
                    </Card>
                    <Typography component="span" color="text.primary">
                      {category.name}
                    </Typography>
                  </Stack>
                </ButtonBase>
              ))}
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  // Process categories based on type and search term
  const processedCategories = categoryItem.map((category: any) => ({
    ...category,
    category_color: sequentialItem(colors),
    category_icon: categoryIconMap[category.name] || <FastfoodIcon />,
    categories: category.categories.map((subCategory: any) => ({
      ...subCategory,
      category_item_color: sequentialItem(colors),
    }))
      .filter((subCategory: any) =>
        normalizeForSearch(subCategory.name).includes(normalizeForSearch(searchTerm))
      ),
  }))
    .filter((category: any) =>
      category.categories.length > 0 ||
      normalizeForSearch(category.name).includes(normalizeForSearch(searchTerm))
    );

  function normalizeForSearch(input: any) {
    if (!input) return '';
    try {
      return String(input)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    } catch (e) {
      return String(input)
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    }
  }

  const renderCategoryItems = () => {
    if (type === 'expense') {
      return processedCategories.map((item, index) => (
        <Accordion defaultExpanded key={index} sx={{ boxShadow: 'none' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel-${index}-content`}
            id={`panel-${index}-header`}
            sx={{
              px: 0,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Card elevation={4} sx={{ borderRadius: '50%' }}>
                <DynamicSvgIcon svgUrl={item.icon} color={item.iconColor} size={30} iconSize={20} />
              </Card>
              <Typography component="span" variant="h2">{item.name}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              '&.MuiAccordionDetails-root': {
                padding: 0,
              },
            }}
          >
            <Stack direction="column" spacing={2} pt={2}>
              <Stack display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} justifyContent="center">
                {item.categories.map((subCategory: any, subIndex: number) => (
                  <ButtonBase
                    key={subIndex}
                    sx={{
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'start',
                      color: subCategory.iconColor || item.iconColor,
                      '&:hover': {
                        color: subCategory.iconColor || item.iconColor,
                      },
                    }}
                    onClick={() => handleSelectCategory(subCategory, item)}
                  >
                    <Stack direction="column" justifyContent="start" alignItems="center" spacing={1} sx={{ cursor: "pointer" }}>
                      <Card
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: subCategory.backgroundColor || subCategory.iconColor || item.iconColor,
                        }}
                      >
                        <img src={subCategory.icon} alt={subCategory.name} style={{ width: 24, height: 24 }} />
                      </Card>
                      <Typography component="span" color="text.primary">
                        {subCategory.name}
                      </Typography>
                    </Stack>
                  </ButtonBase>
                ))}
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ));
    } else {
      // Income category display (flat grid)
      return (
        <Stack spacing={2} mt={4}>
          {processedCategories.map((item, index) => {
            if (item.categories.length === 0) return null;

            return (
              <Stack
                key={index}
                display="grid"
                gridTemplateColumns="repeat(4, 1fr)"
                gap={2}
                justifyContent="center"
              >
                {item.categories.map((subCategory: any, subIndex: number) => (
                  <Stack key={subIndex} alignItems="center" spacing={1}>
                    <ButtonBase
                      sx={{
                        borderRadius: "50%",
                        width: 64,
                        height: 64,
                        backgroundColor: subCategory.backgroundColor || subCategory.iconColor || item.iconColor,
                        color: theme.palette.primary.contrastText,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      onClick={() => handleSelectCategory(subCategory, item)}
                    >
                      <img src={subCategory.icon} alt={subCategory.name} />
                    </ButtonBase>
                    <Typography
                      component="span"
                      fontSize={14}
                      textAlign="center"
                      sx={{ maxWidth: 80 }}
                    >
                      {subCategory.name}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            );
          })}
        </Stack>
      );
    }
  };

  const title = type === 'expense'
    ? t('expense_title', { ns: 'categorySelector', defaultValue: 'Chọn mục chi' })
    : t('income_title', { ns: 'categorySelector', defaultValue: 'Chọn mục thu' });

  const searchField = (
    <TextField
      fullWidth
      variant="outlined"
      placeholder={t('search', { ns: 'categorySelector', defaultValue: 'Tìm kiếm khoản mục...' })}
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      slotProps={{
        input: {
          sx: {
            mb: 2,
            backgroundColor: theme.palette.common.white,
          },
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        },
      }}
    />
  );

  const content = (
    <>
      {searchField}
      {renderFrequentlyUsedSection()}
      {renderCategoryItems()}
    </>
  );

  // Render based on device type
  if (isDesktop) {
    return (
      <BaseDialog
        open={open}
        maxWidth='sm'
        onClose={onClose}
        title={title}
        extraAction={(
          <IconButton onClick={() => setOpenEditCategory(type)} aria-label={t('edit', { ns: 'common' })}>
            <PencilSimpleSlashIcon />
          </IconButton>
        )}
      >
        {content}
      </BaseDialog>
    );
  }

  return (
    <DrawerBottom
      mobileKeyboardAware
      open={open}
      onClose={onClose}
      title={title}
      extraAction={(
        <IconButton onClick={() => setOpenEditCategory(type)} aria-label={t('edit', { ns: 'common' })}>
          <PencilSimpleSlashIcon />
        </IconButton>
      )}
    >
      {content}
    </DrawerBottom>
  );
};

export default CategorySelector; 